/**
 * Les formes sur cahier, **côté parent** (lot P3) : opération posée,
 * construction, dictée, écriture courte.
 *
 * Deux morceaux :
 *
 * - `TexteDuParent` — ce qu'une ligne d'entraînement porte en plus quand son
 *   exercice est une forme papier : la consigne, la durée annoncée, la grille
 *   propre à la forme et, pour une dictée, **le texte en groupes de souffle**
 *   avec le rythme de lecture. Ces champs ne sont remplis que par le signataire
 *   du parent ; la route de l'enfant les rend vides, et un test balaie
 *   `src/child/**` pour affirmer qu'aucun écran de l'enfant ne les lit.
 * - `DicteeDeLaSemaine` — la carte qui propose la dictée du moment, hors
 *   carnet, et la donne à l'enfant d'un geste : le serveur ouvre une feuille
 *   d'une ligne, déjà close, que l'enfant photographie comme les autres.
 *
 * La grille est une **liste à regarder**, pas des cases qui écrivent : le
 * verdict reste les quatre boutons de l'écran « À valider », et c'est lui seul
 * qui écrit une observation (poids 0,8).
 */

import { useCallback, useEffect, useState } from "react";

import { call } from "@/api/client";
import { ApiError } from "@/api/errors";
import type { DicteeDeLaSemaineOut, LigneDEntrainementOut } from "@/api/schema";
import { Button, Card, ErrorNote } from "@/ui/primitives";

type TexteDeForme = Pick<
  LigneDEntrainementOut,
  | "consigne"
  | "duree_annoncee"
  | "corrige_texte"
  | "groupes"
  | "rythme"
  | "grille"
>;

/** Les groupes de souffle, numérotés — l'écran du parent peut porter des
 *  chiffres (§14.1 ne couvre que l'enfant). */
function Groupes({ groupes, rythme }: { groupes: string[]; rythme: string[] }) {
  return (
    <>
      {rythme.length ? (
        <>
          <p className="muted small">Comment la lire :</p>
          <ol className="small">
            {rythme.map((etape) => (
              <li key={etape}>{etape}</li>
            ))}
          </ol>
        </>
      ) : null}
      <p className="muted small">
        Le texte, groupe de souffle par groupe de souffle :
      </p>
      <ol className="dictee__groupes">
        {groupes.map((groupe, rang) => (
          <li key={`${rang}-${groupe}`}>{groupe}</li>
        ))}
      </ol>
    </>
  );
}

function Grille({ grille }: { grille: string[] }) {
  if (!grille.length) return null;
  return (
    <>
      <p className="muted small">À regarder sur la copie :</p>
      <ul className="small">
        {grille.map((point) => (
          <li key={point}>{point}</li>
        ))}
      </ul>
    </>
  );
}

export function TexteDuParent({ ligne }: { ligne: TexteDeForme }) {
  const groupes = ligne.groupes ?? [];
  const grille = ligne.grille ?? [];
  if (
    !ligne.consigne &&
    !groupes.length &&
    !grille.length &&
    !ligne.corrige_texte
  ) {
    return null;
  }
  return (
    <div className="forme-papier">
      {ligne.consigne ? <p>{ligne.consigne}</p> : null}
      {ligne.duree_annoncee ? (
        <p className="muted small">Durée prévue : {ligne.duree_annoncee}.</p>
      ) : null}
      {groupes.length ? (
        <Groupes groupes={groupes} rythme={ligne.rythme ?? []} />
      ) : ligne.corrige_texte ? (
        <p className="muted small">
          Le résultat : <strong>{ligne.corrige_texte}</strong>
        </p>
      ) : null}
      <Grille grille={grille} />
    </div>
  );
}

/**
 * « Dictée de la semaine » : rien quand le serveur n'en a pas à proposer
 * (l'enfant n'y a pas droit, ou aucune n'est disponible) ; une ligne quand une
 * dictée attend déjà son verdict ; sinon le texte et le bouton qui la donne.
 */
export function DicteeDeLaSemaine({
  childId,
  prenom,
  apresDon,
}: {
  childId: string;
  prenom: string | undefined;
  apresDon: () => void;
}) {
  const [dictee, setDictee] = useState<DicteeDeLaSemaineOut | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [panne, setPanne] = useState<string | null>(null);

  const charger = useCallback(async () => {
    try {
      setDictee(
        await call("dicteeDeLaSemaine", { params: { child_id: childId } }),
      );
    } catch {
      setDictee(null);
    }
  }, [childId]);

  useEffect(() => {
    void charger();
  }, [charger]);

  if (!dictee) return null;
  if (dictee.en_attente) {
    return (
      <Card title="Dictée de la semaine">
        <p className="muted small">
          Une dictée attend encore votre verdict : la suivante viendra après.
        </p>
      </Card>
    );
  }
  if (!dictee.disponible) return null;

  const donner = async () => {
    setEnvoi(true);
    setPanne(null);
    try {
      await call("donnerLaDictee", { params: { child_id: childId } });
      await charger();
      apresDon();
    } catch (echec) {
      setPanne(
        echec instanceof ApiError
          ? echec.message
          : "La dictée n'est pas partie.",
      );
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <Card title="Dictée de la semaine">
      <p>
        <strong>{dictee.titre}</strong>
        {dictee.chapitre ? (
          <span className="muted small"> · {dictee.chapitre}</span>
        ) : null}
      </p>
      <TexteDuParent
        ligne={{
          consigne: dictee.consigne ?? "",
          duree_annoncee: dictee.duree_annoncee ?? null,
          groupes: dictee.groupes ?? [],
          rythme: dictee.rythme ?? [],
          grille: dictee.grille ?? [],
        }}
      />
      {panne ? <ErrorNote>{panne}</ErrorNote> : null}
      <div className="list__actions">
        <span className="muted small">
          {prenom ?? "L'enfant"} ne verra que la consigne, jamais le texte.
        </span>
        <Button disabled={envoi} onClick={() => void donner()}>
          Donner la dictée
        </Button>
      </div>
    </Card>
  );
}
